import React from 'react'

const PRIORITY_STYLES = {
  info: { border: 'border-blue-500', icon: 'text-blue-400', bg: 'bg-blue-500/10', label: 'Information' },
  warning: { border: 'border-amber-500', icon: 'text-amber-400', bg: 'bg-amber-500/10', label: 'Warning' },
  critical: { border: 'border-red-500', icon: 'text-red-400', bg: 'bg-red-500/10', label: 'Action Required' },
} as const

interface AnnouncementPreviewModalProps {
  title: string
  body: string | null
  priority: 'info' | 'warning' | 'critical'
  onClose: () => void
}

export function AnnouncementPreviewModal({ title, body, priority, onClose }: AnnouncementPreviewModalProps): React.ReactElement {
  const style = PRIORITY_STYLES[priority]

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      {/* Preview label */}
      <div className="absolute top-6 left-1/2 -translate-x-1/2 flex items-center gap-2 px-4 py-2 bg-slate-800 border border-slate-600 rounded-full shadow-lg z-10">
        <svg className="w-4 h-4 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
          <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
        <span className="text-xs font-semibold text-slate-300 uppercase tracking-wider">Dealer Preview</span>
      </div>

      {/* Simulated popup - light theme like the dealer VMS */}
      <div className={`w-full max-w-lg bg-white rounded-2xl shadow-2xl border-t-4 ${style.border} overflow-hidden`}>
        {/* Header */}
        <div className={`px-6 pt-6 pb-4 ${style.bg}`}>
          <div className="flex items-start gap-3">
            <div className={`w-10 h-10 rounded-full ${style.bg} flex items-center justify-center shrink-0 border ${style.border}`}>
              {priority === 'info' && (
                <svg className={`w-5 h-5 ${style.icon}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z" />
                </svg>
              )}
              {priority === 'warning' && (
                <svg className={`w-5 h-5 ${style.icon}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
                </svg>
              )}
              {priority === 'critical' && (
                <svg className={`w-5 h-5 ${style.icon}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
                </svg>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p className={`text-xs font-semibold uppercase tracking-wider ${style.icon} mb-1`}>{style.label}</p>
              <h2 className="text-lg font-bold text-gray-900">{title || 'Untitled Announcement'}</h2>
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="px-6 py-5">
          {body ? (
            <div
              className="text-sm text-gray-700 leading-relaxed prose prose-sm max-w-none"
              dangerouslySetInnerHTML={{ __html: body }}
            />
          ) : (
            <p className="text-sm text-gray-400 italic">No message content</p>
          )}
        </div>

        {/* Footer - simulated dealer action */}
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex items-center justify-end gap-3">
          <button
            type="button"
            className="px-4 py-2 text-sm font-medium text-gray-500 hover:text-gray-700 transition-colors cursor-default"
            tabIndex={-1}
          >
            Remind me later
          </button>
          <button
            type="button"
            className="px-5 py-2 text-sm font-semibold text-white bg-[#094673] rounded-lg cursor-default"
            tabIndex={-1}
          >
            Acknowledge
          </button>
        </div>
      </div>

      {/* Close button */}
      <button
        type="button"
        onClick={onClose}
        className="absolute top-6 right-6 p-2 text-slate-400 hover:text-white bg-slate-800 hover:bg-slate-700 rounded-full border border-slate-600 transition-colors"
      >
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>
  )
}
